import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  NgZone,
  OnChanges,
  SimpleChanges,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import type * as MonacoEditor from 'monaco-editor';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { timer } from 'rxjs';

import { isDefined } from '@shared/utils/is-defined';

import { JsonEditorService } from './json-editor.service';
import { JsonEditorViewModel } from './json-editor.view-model';

declare global {
  interface Window {
    monaco: typeof MonacoEditor;
  }
}

@Component({
  selector: 'app-json-editor',
  templateUrl: './json-editor.component.html',
  styleUrls: ['./json-editor.component.scss'],
  imports: [MonacoEditorModule, FormsModule],
  providers: [JsonEditorService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JsonEditorComponent implements OnChanges, AfterViewInit {
  readonly #service = inject(JsonEditorService);
  readonly #cdRef = inject(ChangeDetectorRef);
  readonly #ngZone = inject(NgZone);
  readonly #destroyRef = inject(DestroyRef);

  public label = input<string>();
  public isRequired = input<boolean>(false);
  public vm = input<JsonEditorViewModel>();
  public customErrorMessage = input<string>();
  public readOnly = input<boolean>(false);
  public language = input<string>('json');
  public contentChange = output<string>();
  public errorsChange = output<string[]>();

  public isDirty = false;

  public editorOptions?: MonacoEditor.editor.IStandaloneEditorConstructionOptions;

  public fileContent?: string;

  public errors: string[] = [];

  private editor?: MonacoEditor.editor.IStandaloneCodeEditor;

  private initialFileContent?: string;
  private firstRealFileContentChangeHappened = false;

  public get allErrors(): string[] {
    const customErrorMessage = this.customErrorMessage();
    return [...(customErrorMessage ? [customErrorMessage] : []), ...this.errors];
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const vm = this.vm();
    if (changes['vm'] && vm && !isDefined(this.editorOptions)) {
      this.initialFileContent = vm.content;
      this.initEditorOptions(vm);
    }

    if (changes['vm'] && vm && vm.content !== this.fileContent) {
      this.fileContent = vm.content;
    }

    if (changes['customErrorMessage']) {
      this.updateMonacoEditorLayoutAfterRender();
    }
  }

  public ngAfterViewInit(): void {
    this.updateMonacoEditorLayoutAfterRender();
  }

  public onFileContentChange(newFileContent: string): void {
    /**
     * The monaco editor tend to fire multiple change events without actual content change
     */
    if (!this.firstRealFileContentChangeHappened && this.initialFileContent === newFileContent) {
      return;
    }

    this.firstRealFileContentChangeHappened = true;
    this.isDirty = true;
    this.contentChange.emit(newFileContent);
  }

  public onEditorInit(editor: MonacoEditor.editor.IStandaloneCodeEditor): void {
    this.editor = editor;
    this.startListeningForEditorErrors(this.editor);
  }

  private initEditorOptions(vm: JsonEditorViewModel): void {
    this.editorOptions = {
      language: this.language(),
      theme: 'vs-dark',
      readOnly: this.readOnly(),
      padding: {
        top: 16,
        bottom: 16,
      },
      minimap: {
        enabled: false,
      },
    };
    this.fileContent = vm.content;
  }

  private startListeningForEditorErrors(editor: MonacoEditor.editor.IStandaloneCodeEditor): void {
    this.#service
      .getEditorErrors$(editor)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((errors) => {
        this.#ngZone.run(() => {
          this.errors = errors;
          this.errorsChange.emit(errors);
          this.#cdRef.markForCheck();
        });
        this.editor!.layout();
      });
  }

  private updateMonacoEditorLayoutAfterRender(): void {
    timer(0)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.editor?.layout();
      });
  }
}
